import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import { useActivityTracker } from './hooks/useActivityTracker';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import UsersListPage from './pages/users/UsersListPage';
import UserDetailsPage from './pages/users/UserDetailsPage';
import UserNavigationHistoryPage from './pages/users/UserNavigationHistoryPage';
import UserFormPage from './pages/users/UserFormPage';
import BizuListPage from './pages/bizu/BizuListPage';
import BizuDetailPage from './pages/bizu/BizuDetailPage';
import QuesoLibraryPage from './pages/bizu/QuesoLibraryPage';
import KienPlayedRecipesPage from './pages/bizu/KienPlayedRecipesPage';
import KienUserRecipesPage from './pages/bizu/KienUserRecipesPage';
import RecipesListPage from './pages/recipes/RecipesListPage';
import RecipeFormPage from './pages/recipes/RecipeFormPage';
import RecipeIngredientsPage from './pages/recipes/RecipeIngredientsPage';
import IngredientsListPage from './pages/ingredients/IngredientsListPage';
import ProductsListPage from './pages/products/ProductsListPage';
import ProductFormPage from './pages/products/ProductFormPage';
import AdminsListPage from './pages/admins/AdminsListPage';
import AdminFormPage from './pages/admins/AdminFormPage';
import RolesListPage from './pages/roles/RolesListPage';
import ActivityLogPage from './pages/activity/ActivityLogPage';
import AdminActivityPage from './pages/activity/AdminActivityPage';
import AnalyticsOverviewPage from './pages/analytics/AnalyticsOverviewPage';
import CostTrackingPage from './pages/analytics/CostTrackingPage';
import OpenAILogsPage from './pages/analytics/OpenAILogsPage';
import RateLimitsPage from './pages/analytics/RateLimitsPage';
import AudiosPage from './pages/analytics/AudiosPage';

function App() {
  const { isAuthenticated, loading } = useAuth();

  // Track admin page visits
  useActivityTracker();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
      />
      <Route
        path="/reset-password/:token"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />}
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Users */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/new"
        element={
          <ProtectedRoute>
            <UserFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id"
        element={
          <ProtectedRoute>
            <UserDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id/edit"
        element={
          <ProtectedRoute>
            <UserFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:id/navigation-history"
        element={
          <ProtectedRoute>
            <UserNavigationHistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Bizu Characters */}
      <Route
        path="/bizu"
        element={
          <ProtectedRoute>
            <BizuListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bizu/queso/library/:userId"
        element={
          <ProtectedRoute>
            <QuesoLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bizu/kien/played-recipes/:userId"
        element={
          <ProtectedRoute>
            <KienPlayedRecipesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bizu/kien/user-recipes/:userId"
        element={
          <ProtectedRoute>
            <KienUserRecipesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bizu/:name"
        element={
          <ProtectedRoute>
            <BizuDetailPage />
          </ProtectedRoute>
        }
      />


      {/* Recipes */}
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <RecipesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/new"
        element={
          <ProtectedRoute requireAdmin>
            <RecipeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id/edit"
        element={
          <ProtectedRoute requireAdmin>
            <RecipeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id/ingredients"
        element={
          <ProtectedRoute>
            <RecipeIngredientsPage />
          </ProtectedRoute>
        }
      />

      {/* Ingredients */}
      <Route
        path="/ingredients"
        element={
          <ProtectedRoute>
            <IngredientsListPage />
          </ProtectedRoute>
        }
      />

      {/* Products */}
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            <ProductFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            <ProductFormPage />
          </ProtectedRoute>
        }
      />

      {/* Admins - Admin and SuperAdmin */}
      <Route
        path="/admins"
        element={
          <ProtectedRoute requireAdmin>
            <AdminsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins/new"
        element={
          <ProtectedRoute requireAdmin>
            <AdminFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins/:id/edit"
        element={
          <ProtectedRoute requireAdmin>
            <AdminFormPage />
          </ProtectedRoute>
        }
      />

      {/* Roles - SuperAdmin only */}
      <Route
        path="/roles"
        element={
          <ProtectedRoute requireSuperAdmin>
            <RolesListPage />
          </ProtectedRoute>
        }
      />

      {/* Activity Log - Admin and SuperAdmin */}
      <Route
        path="/activity"
        element={
          <ProtectedRoute requireAdmin>
            <ActivityLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity/:id"
        element={
          <ProtectedRoute requireAdmin>
            <AdminActivityPage />
          </ProtectedRoute>
        }
      />

      {/* Analytics - Admin and SuperAdmin */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute requireAdmin>
            <AnalyticsOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/costs"
        element={
          <ProtectedRoute requireAdmin>
            <CostTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/logs"
        element={
          <ProtectedRoute requireAdmin>
            <OpenAILogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/rate-limits"
        element={
          <ProtectedRoute requireAdmin>
            <RateLimitsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/audios"
        element={
          <ProtectedRoute requireAdmin>
            <AudiosPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
